import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  WebhookEventsResponseDto,
  WebhookEventsQuery,
  WebhookEventDetailsDto,
} from '../types';

const QUERY_KEY_WEBHOOK_EVENTS = 'webhook-events';
const QUERY_KEY_WEBHOOK_DETAILS = 'webhook-event-details';

/**
 * Hook for fetching webhook events with infinite scroll
 */
export function useWebhookEvents(filters?: WebhookEventsQuery) {
  return useInfiniteQuery<WebhookEventsResponseDto>({
    queryKey: [QUERY_KEY_WEBHOOK_EVENTS, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        ...filters,
        offset: pageParam as number,
        limit: filters?.limit ?? 20,
      };

      const { data } = await api.get<WebhookEventsResponseDto>(
        '/webhook-logs',
        { params },
      );

      return data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return (lastPage.items.length || 0);
      }
      return undefined;
    },
  });
}

/**
 * Hook for fetching webhook event details (includes full payload)
 */
export function useWebhookEventDetails(eventId: string | undefined) {
  return useQuery<WebhookEventDetailsDto>({
    queryKey: [QUERY_KEY_WEBHOOK_DETAILS, eventId],
    queryFn: async () => {
      const { data } = await api.get<WebhookEventDetailsDto>(
        `/webhook-logs/${eventId}`,
      );
      return data;
    },
    enabled: !!eventId, // Only run if eventId is truthy
  });
}
